import React, { useContext, useMemo, useState } from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import { useQuery } from '@tanstack/react-query';

import { RootState } from '../../../app/rootReducer';
import { getRuleResultsEndPoint } from '../../../common/api.endPoints';
import { ApplicationLifecycle } from '../../../common/constants/lifecycle';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { HttpService } from '../../../common/http.service';
import { IRuleResults } from '../interfaces/IRuleResults';
import { aggregateRuwReasons } from './helpers';
import styles from './ruwReasons.module.scss';
import { DEFAULT_VISIBLE_REASONS, ReferralReason } from './types';

const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

interface RuwReasonsProps {
  applicationId: string;
}

export const RuwReasons: React.FC<RuwReasonsProps> = ({ applicationId }) => {
  const applicationLifecycle = useSelector(
    state => state.application.applicationLifecycleV2,
  );
  const isRecommendedForRescission = useSelector(
    state => state.application.isRecommendedForRescission,
  );
  const decisions = useSelector(state => state.decision.decisions);

  const { flags } = useContext(FeatureFlagsContext);
  const ruwReasonsEnabled = flags.displayRuwReasonsEnabled || false;

  const isRuw = applicationLifecycle === ApplicationLifecycle.RUW;

  const { data: ruleResults } = useQuery<IRuleResults | undefined>({
    queryKey: ['ruleResults', applicationId, 'Final'],
    enabled: isRuw && ruwReasonsEnabled,
    queryFn: async () => {
      const response = await HttpService.getInstance().get(
        getRuleResultsEndPoint(applicationId, 'Final'),
      );
      if (response && !response.error) {
        return response.parsedBody as IRuleResults;
      }
      return undefined;
    },
  });

  const reasons = useMemo<ReferralReason[]>(
    () =>
      aggregateRuwReasons({
        ruleResults,
        decisions: decisions ?? [],
        isRecommendedForRescission: !!isRecommendedForRescission,
      }),
    [ruleResults, decisions, isRecommendedForRescission],
  );

  const [showAll, setShowAll] = useState(false);

  if (!ruwReasonsEnabled || !isRuw || reasons.length === 0) return null;

  const visibleReasons = showAll
    ? reasons
    : reasons.slice(0, DEFAULT_VISIBLE_REASONS);
  const hiddenCount = reasons.length - DEFAULT_VISIBLE_REASONS;

  return (
    <section
      className={styles.section}
      data-section-id="ruw-reasons"
      aria-label="Reasons referred to underwriting"
    >
      <div className={styles.header}>
        <span className={styles.diamond} aria-hidden="true" />
        <span>
          {reasons.length} reasons referred to underwriting — review all before
          decision
        </span>
      </div>
      <div className={styles.grid}>
        {visibleReasons.map(reason => (
          <div key={reason.id} className={styles.card}>
            <p className={styles.cardTitle}>{reason.title}</p>
            <p className={styles.cardBody}>{reason.body}</p>
          </div>
        ))}
      </div>
      {hiddenCount > 0 && (
        <button
          type="button"
          className={styles.showMore}
          onClick={() => setShowAll(prev => !prev)}
          aria-expanded={showAll}
        >
          {showAll
            ? 'Show fewer reasons ▲'
            : `Show ${hiddenCount} more reason${hiddenCount === 1 ? '' : 's'} ▼`}
        </button>
      )}
    </section>
  );
};
